import { useEffect } from "react";
import { tableService } from "../../../../../../app/services/tableService";
import { UpdateTabledata } from "../../../../../../app/services/tableService/updateTable";
import { useFormContext } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../../../components/ui/use-toast";
import { FormEditTableData } from "../TableCard/useTableCardController";
import { useAtom } from "jotai";
import { selectedTableCardInfo } from "../../../../../../app/store";
import { formatPercentage } from "@/app/utils/comissionPercentage";
import { formatMoneyValue } from "@/app/utils/formatMoneyValue";

export const useEditTableCardController = () => {
  const methods = useFormContext<FormEditTableData>();
  const [selectedTableCard] = useAtom(selectedTableCardInfo);
  const queryClient = useQueryClient();

  const { toast } = useToast();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["updateTable"],
    mutationFn: async ({ data, id }: { data: UpdateTabledata; id: string }) => {
      return tableService.updateTable(data, id);
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["allTables"],
      });
    },
  });

  const { reset } = methods;

  const isCLT = selectedTableCard?.type === "CLT";

  useEffect(() => {
    if (selectedTableCard) {
      const {
        bonus,
        percents,
        name,
        limit,
        maxPercents,
        leaderCommission,
        leaderBonus,
      } = selectedTableCard;
      reset({
        name,
        percents: formatPercentage(percents * 100),
        maxPercents: formatPercentage((maxPercents ?? 0) * 100),
        bonus: formatPercentage(bonus * 100 || 0),
        leaderCommission: formatPercentage((leaderCommission ?? 0) * 100),
        leaderBonus: formatPercentage((leaderBonus ?? 0) * 100),
        limit: formatMoneyValue(limit || 0),
        valuesAndCommissions: isCLT
          ? (selectedTableCard.valuesAndCommissions ?? []).map((item) => ({
              value: String(item.value),
              commission: Number(item.commission ?? 0).toFixed(2),
              leaderCommission: Number(item.leaderCommission ?? 0).toFixed(2),
              max:
                item.max !== undefined
                  ? Number(item.max).toFixed(2)
                  : undefined,
            }))
          : undefined,
      });
    }
  }, [reset]);

  const parsePercent = (value?: string) =>
    value ? Number(String(value).replace("%", "").replace(",", ".").trim()) : 0;

  const handleEditTableSubmit = async (data: FormEditTableData) => {
    try {
      if (selectedTableCard._id) {
        const { valuesAndCommissions, ...rest } = data;

        const formattedData = {
          ...rest,
          percents: parsePercent(data.percents),
          maxPercents: parsePercent(data.maxPercents),
          bonus: parsePercent(data.bonus),
          leaderCommission: parsePercent(data.leaderCommission),
          leaderBonus: parsePercent(data.leaderBonus),
          limit: data.limit ? Number(data.limit.replace(/\D/g, "")) : 0,
          ...(isCLT && {
            valuesAndCommissions: (valuesAndCommissions ?? []).map((item) => ({
              value: Number(String(item.value).replace(/\D/g, "")),
              commission: parsePercent(item.commission),
              leaderCommission: parsePercent(item.leaderCommission),
              max: parsePercent(item.max),
            })),
          }),
        };
        const response = await mutateAsync({
          data: formattedData,
          id: selectedTableCard._id,
        });

        if (response.name === data.name) {
          toast({
            title: "Dados atualizados com sucesso!",
          });
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log(error);
      toast({
        variant: "destructive",
        title:
          error?.message ||
          "Ocorreu um erro ao tentar atualizar os dados, tente novamente mais tarde.",
      });
    }
  };

  return {
    methods,
    isPending,
    handleEditTableSubmit,
    selectedTableCard,
  };
};
